import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type Country, supportedCountries } from '../../services/Country';
import type { ReduxState } from '../../store';
import { setShowTopBanner } from '../../store/app';
import { useRegion, useRestriction } from '../Restriction';
import T from '../Translate';

import styles from './topBanner.module.scss';

type Props = {
  timeout?: number;
  hide?: boolean;
};

const showInRegions = (process.env.GATSBY_SHOW_TOPBANNER_REGION?.split(',') as Country[]) ?? supportedCountries;

export default function TopBanner({ hide, timeout = 2000 }: Props) {
  const dispatch = useDispatch();
  const state = useSelector((state: ReduxState) => ({
    showTopBanner: state.app.showTopBanner,
    topBannerClosedUntilTime: state.app.topBannerClosedUntilTime,
  }));
  const restricted = useRestriction({ region: showInRegions, only: true });
  const [region] = useRegion();
  const timeoutId = useRef(null);

  const handleOnCloseClick = () => {
    dispatch(setShowTopBanner(false));
  };

  useEffect(() => {
    if (restricted || hide || Date.now() < (state.topBannerClosedUntilTime ?? 0)) {
      return;
    }
    timeoutId.current = setTimeout(() => {
      dispatch(setShowTopBanner(true));
    }, timeout);

    return () => {
      if (!timeoutId.current) return;
      clearTimeout(timeoutId.current);
    };
  }, [state.topBannerClosedUntilTime, hide, timeout, restricted]);

  if (restricted) return null;

  return (
    <div className={`${styles.topBannerContainer} ${state.showTopBanner ? styles.topBannerContainerOpen : ''}`}>
      <div className={styles.topBanner}>
        <T context={region.toLowerCase()} html id="global.topBanner" />
      </div>
      <button className={`${styles.close} m-0 p-0`} onClick={handleOnCloseClick} type="button">
        <i className="icon icon-close" />
      </button>
    </div>
  );
}
